import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ChevronRight } from 'lucide-react'
import { useDocumentHead } from '@/hooks/use-document-head'
import { useHeaderExtra } from '@/contexts/header.context'
import { evalApiClient } from '@/lib/api/eval.client'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { EvalResultDetail } from '@super/shared-types'
import { applyMatcher } from '@super/eval-framework'

// ---------------------------------------------------------------------------
// Semantic JSON diff
// ---------------------------------------------------------------------------

type DiffLine =
  | { kind: 'same'; key: string; value: unknown }
  | { kind: 'changed'; key: string; expected: unknown; actual: unknown }
  | { kind: 'removed'; key: string; value: unknown }
  | { kind: 'added'; key: string; value: unknown }

function buildDiff(
  expected: Record<string, unknown>,
  actual: Record<string, unknown> | null | undefined,
): DiffLine[] {
  const lines: DiffLine[] = []
  const allKeys = new Set([
    ...Object.keys(expected),
    ...Object.keys(actual ?? {}),
  ])

  for (const key of allKeys) {
    const inExpected = key in expected
    const inActual = actual != null && key in actual

    if (inExpected && inActual) {
      const ev = expected[key]
      const av = (actual as Record<string, unknown>)[key]
      // ev is always a MatcherSpec — use matcher semantics, not raw equality
      const isMatcherSpec =
        typeof ev === 'object' &&
        ev !== null &&
        !Array.isArray(ev) &&
        Object.keys(ev as object).some(k => k.startsWith('$'))
      const matches = isMatcherSpec
        ? applyMatcher(ev as Record<string, unknown>, av)
        : JSON.stringify(ev) === JSON.stringify(av)
      if (matches) {
        lines.push({ kind: 'same', key, value: ev })
      } else {
        lines.push({ kind: 'changed', key, expected: ev, actual: av })
      }
    } else if (inExpected) {
      lines.push({ kind: 'removed', key, value: expected[key] })
    } else {
      lines.push({
        kind: 'added',
        key,
        value: (actual as Record<string, unknown>)[key],
      })
    }
  }

  return lines
}

function formatVal(v: unknown): string {
  if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
    const s = v as Record<string, unknown>
    if ('$empty' in s) return s.$empty === false ? 'not empty' : 'empty'
    if ('$in' in s)
      return `any of [${(s.$in as unknown[]).map(x => JSON.stringify(x)).join(', ')}]`
    if ('$contains' in s) return `contains "${s.$contains}"`
    if ('$gt' in s) return `> ${s.$gt}`
    if ('$gte' in s) return `≥ ${s.$gte}`
    if ('$lt' in s) return `< ${s.$lt}`
    if ('$lte' in s) return `≤ ${s.$lte}`
    if ('$exact' in s) return JSON.stringify(s.$exact)
  }
  if (typeof v === 'string') return `"${v}"`
  if (v === null) return 'null'
  return JSON.stringify(v)
}

function JsonDiff({
  expected,
  actual,
}: {
  expected: Record<string, unknown>
  actual: Record<string, unknown> | null | undefined
}) {
  const lines = buildDiff(expected, actual)

  return (
    <div className="rounded-md border overflow-hidden font-mono text-sm">
      {/* Header row */}
      <div className="grid grid-cols-2 border-b bg-muted/50">
        <div className="px-3 py-1.5 text-xs text-muted-foreground font-sans border-r">
          Expected
        </div>
        <div className="px-3 py-1.5 text-xs text-muted-foreground font-sans">
          Actual
        </div>
      </div>

      {lines.map((line, i) => {
        if (line.kind === 'same') {
          return (
            <div key={i} className="grid grid-cols-2 border-b last:border-0">
              <div className="px-3 py-1 border-r text-muted-foreground">
                <span className="select-none mr-2 text-muted-foreground/40">
                  {' '}
                </span>
                <span className="text-foreground">{line.key}</span>
                <span className="text-muted-foreground">
                  : {formatVal(line.value)}
                </span>
              </div>
              <div className="px-3 py-1 text-muted-foreground">
                <span className="select-none mr-2 text-muted-foreground/40">
                  {' '}
                </span>
                <span className="text-foreground">{line.key}</span>
                <span className="text-muted-foreground">
                  : {formatVal(line.value)}
                </span>
              </div>
            </div>
          )
        }

        if (line.kind === 'changed') {
          return (
            <div key={i} className="grid grid-cols-2 border-b last:border-0">
              <div className="px-3 py-1 bg-error-subtle border-r">
                <span className="select-none mr-2 text-error-dim">-</span>
                <span className="text-foreground">{line.key}</span>
                <span className="text-error">: {formatVal(line.expected)}</span>
              </div>
              <div className="px-3 py-1 bg-success-subtle">
                <span className="select-none mr-2 text-success-dim">+</span>
                <span className="text-foreground">{line.key}</span>
                <span className="text-success">: {formatVal(line.actual)}</span>
              </div>
            </div>
          )
        }

        if (line.kind === 'removed') {
          return (
            <div key={i} className="grid grid-cols-2 border-b last:border-0">
              <div className="px-3 py-1 bg-error-subtle border-r">
                <span className="select-none mr-2 text-error-dim">-</span>
                <span className="text-foreground">{line.key}</span>
                <span className="text-error">: {formatVal(line.value)}</span>
              </div>
              <div className="px-3 py-1">
                <span className="select-none mr-2 opacity-0">-</span>
              </div>
            </div>
          )
        }

        // added
        return (
          <div key={i} className="grid grid-cols-2 border-b last:border-0">
            <div className="px-3 py-1 border-r">
              <span className="select-none mr-2 opacity-0">+</span>
            </div>
            <div className="px-3 py-1 bg-success-subtle">
              <span className="select-none mr-2 text-success-dim">+</span>
              <span className="text-foreground">{line.key}</span>
              <span className="text-success">: {formatVal(line.value)}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function formatRunDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function EvalResultDetailPage() {
  const { runId, resultId } = useParams<{ runId: string; resultId: string }>()
  const [result, setResult] = useState<EvalResultDetail | null>(null)

  useDocumentHead({
    title: result ? `Result · ${result.case.name}` : 'Result Detail',
  })

  useHeaderExtra(
    <div className="flex items-center gap-1.5 text-sm">
      <Link
        to={`/evaluations/runs/${runId}`}
        className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-accent transition-colors"
      >
        <ArrowLeft className="w-4 h-4 text-muted-foreground" />
      </Link>
      <Link
        to="/evaluations?tab=runs"
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        Runs
      </Link>
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
      <Link
        to={`/evaluations/runs/${runId}`}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        {result ? formatRunDate(result.run.createdAt) : '…'}
      </Link>
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
      <span className="text-foreground">{result?.case.name ?? '…'}</span>
    </div>,
    [result?.run.createdAt, result?.case.name, runId],
  )

  useEffect(() => {
    if (!resultId) return
    evalApiClient.getResultById(resultId).then(r => {
      if (r) setResult(r)
    })
  }, [resultId])

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="p-6 pb-24">
        {!result ? (
          <div className="flex flex-col gap-6 max-w-3xl">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-md border p-3 flex flex-col gap-2"
                >
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-5 w-20" />
                </div>
              ))}
            </div>
            <Skeleton className="h-48 w-full" />
          </div>
        ) : (
          <div className="flex flex-col gap-6 max-w-3xl">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                {
                  label: 'Result',
                  value: (
                    <Badge
                      className={
                        result.passed
                          ? 'bg-success-muted text-success'
                          : 'bg-error-muted text-error'
                      }
                    >
                      {result.passed ? 'Pass' : 'Fail'}
                    </Badge>
                  ),
                },
                {
                  label: 'Confidence',
                  value:
                    result.confidence != null
                      ? `${(result.confidence * 100).toFixed(0)}%`
                      : '—',
                },
                {
                  label: 'Duration',
                  value:
                    result.durationMs != null ? `${result.durationMs}ms` : '—',
                },
                {
                  label: 'Cost',
                  value:
                    result.costUsd != null
                      ? `$${result.costUsd.toFixed(5)}`
                      : '—',
                },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <div className="font-medium text-sm mt-0.5">{value}</div>
                </div>
              ))}
            </div>

            {/* Token usage */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Prompt tokens
                </span>
                <span className="font-mono">
                  {result.promptTokens?.toLocaleString() ?? '—'}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Completion tokens
                </span>
                <span className="font-mono">
                  {result.completionTokens?.toLocaleString() ?? '—'}
                </span>
              </div>
            </div>

            {/* Error */}
            {result.error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Error
                </p>
                <pre className="text-sm text-destructive whitespace-pre-wrap font-mono">
                  {result.error}
                </pre>
              </div>
            )}

            {/* LLM details from response */}
            {result.details && (
              <div className="flex flex-col gap-2">
                <span className="text-muted-foreground text-xs uppercase tracking-wide">
                  Match details
                </span>
                <p className="text-sm text-muted-foreground">
                  {result.details}
                </p>
              </div>
            )}

            {/* Diff */}
            <div className="flex flex-col gap-2">
              <span className="text-muted-foreground text-xs uppercase tracking-wide">
                Output diff
              </span>
              <JsonDiff
                expected={result.expectedOutput as Record<string, unknown>}
                actual={result.actualOutput as Record<string, unknown> | null}
              />
            </div>

            {/* Case context */}
            <div className="flex flex-col gap-2">
              <span className="text-muted-foreground text-xs uppercase tracking-wide">
                Case content
              </span>
              <pre className="rounded-md border bg-muted p-4 text-sm whitespace-pre-wrap break-words font-mono">
                {result.case.content}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
